import {yupResolver} from "@hookform/resolvers/yup";
import isNumber from "lodash/isNumber";
import React, {useEffect} from "react";
import {FieldErrors, FormProvider, useForm} from "react-hook-form";
import * as Yup from "yup";

import {Divider} from "@/components/divider/Divider.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {SelectField} from "@/components/form/fields/SelectField/SelectField.tsx";
import FormErrorAlerts from "@/components/form/FormErrorAlerts.tsx";
import {Select} from "@/components/select/Select.tsx";
import {STATUS_PAGE_COMPONENT_URL} from "@/lib/links.ts";
import {ServerErrors} from "@/lib/types/common.ts";
import {Component, ComponentStatus, ComponentStatusValue} from "@/lib/types/statuspages.ts";
import {ServiceSelectField} from "@/pages/checks/components/form/fields/ServiceSelectField.tsx";
import {NO_CHANGE_OPTION} from "@/pages/statuspages/tabs/components/ComponentStatusSelect/ComponentStatusSelect.constants.ts";
import {ComponentStatusSelectField} from "@/pages/statuspages/tabs/components/ComponentStatusSelectField/ComponentStatusSelectField.tsx";

export interface FormValues {
  id?: number;
  name: string;
  description: string;
  group?: number;
  service?: number;
  auto_status_down?: string;
  auto_status_up?: string;
}

export type FormValuesKey = keyof FormValues;

const FormSchema = Yup.object({
  id: Yup.number().optional(),
  name: Yup.string().required("This field is required"),
  description: Yup.string().defined(),
  group: Yup.number().optional(),
  service: Yup.number().optional(),
  auto_status_down: Yup.string()
    .optional()
    .when("service", {
      is: (s: string) => isNumber(s),
      then: () => Yup.string().defined(),
    }),
  auto_status_up: Yup.string()
    .optional()
    .when("service", {
      is: (s: string) => isNumber(s),
      then: () => Yup.string().defined(),
    }),
});

interface AddNewComponentFormProps {
  onSubmit: (d: FormValues) => void;
  componentGroups: Component[];
  component?: Component;
  componentStatuses: ComponentStatus[];
  serverErrors?: ServerErrors;
  onError?: (errors: FieldErrors<FormValues>, event?: React.BaseSyntheticEvent) => void;
  id?: string;
}

const StatusPageUpdateComponentForm = ({
  id,
  onSubmit,
  onError,
  serverErrors,
  component,
  componentStatuses,
  componentGroups,
}: AddNewComponentFormProps) => {
  const [autoStatusUp, setAutoStatusUp] = React.useState<ComponentStatusValue>(ComponentStatusValue.OPERATIONAL);
  const [autoStatusDown, setAutoStatusDown] = React.useState<ComponentStatusValue>(ComponentStatusValue.MAJOR_OUTAGE);

  const methods = useForm<FormValues>({
    resolver: yupResolver<FormValues>(FormSchema),
    defaultValues: {
      id: component?.id,
      name: component?.name,
      group: component?.group ?? undefined,
      description: component?.description,
      //This should be a full service object. only having the id over complicates the form select.
      service: component?.service ?? undefined,
      auto_status_down: component?.auto_status_down ? component.auto_status_down : ComponentStatusValue.NO_CHANGE,
      auto_status_up: component?.auto_status_up ? component.auto_status_up : ComponentStatusValue.NO_CHANGE,
    },
  });

  const {register, handleSubmit, setError, setValue, watch} = methods;

  useEffect(() => {
    if (serverErrors) {
      Object.keys(serverErrors)
        .filter((k) => k !== "__all__")
        .forEach((key) => {
          setError(key as FormValuesKey, {message: serverErrors[key][0]});
        });
    }
  }, [setError, serverErrors]);

  return (
    <FormProvider {...methods}>
      <form id={id} onSubmit={handleSubmit(onSubmit, onError)}>
        {serverErrors?.__all__ && <FormErrorAlerts errors={serverErrors?.__all__} />}
        <div className={"d-flex flex-column gap-4"}>
          <div className="pulseTextBody">
            Components represent the functional parts of your infrastructure, such as websites, API endpoints, or
            databases.&nbsp;
            <a target="_blank" href={STATUS_PAGE_COMPONENT_URL}>
              View Documentation
            </a>
          </div>
          {component && <input type="number" hidden {...register("id")} />}
          <InputField
            name={"name"}
            required
            placeholder="e.g. Website, Database, API"
            label={"Component Name"}
            autoFocus
          />
          <SelectField
            name={"group"}
            label={"Component Group"}
            tooltip={"Select an existing Component Group for this Component, or “Add Group” on the prior screen."}
            isClearable
            select={
              <Select
                options={componentGroups}
                onChange={(v) => {
                  setValue("group", v?.id);
                }}
                getOptionValue={(c) => c.id.toString()}
                getOptionLabel={(c) => c.name}
                defaultValue={componentGroups.find((c) => c.id === component?.group)}
              />
            }
          />
          <InputField
            name={"description"}
            label={"Component Description"}
            tooltip={"OPTIONAL: Provide a description of this Component for Page visitors."}
          />
          <Divider />
          <ServiceSelectField
            name={"service"}
            label={"Associated Check"}
            tooltip={
              "OPTIONAL: Link this Component to a Check to display response time. Checks can also affect Component status."
            }
            isClearable={true}
            defaultValue={
              component?.service
                ? {
                    id: component.service,
                    name: component.service_name,
                    type: component.service_type,
                    address: component.service_address,
                  }
                : undefined
            }
            onChange={(v) => {
              if (!v) {
                setValue("auto_status_up", ComponentStatusValue.NO_CHANGE);
                setValue("auto_status_down", ComponentStatusValue.NO_CHANGE);
              } else {
                setValue("auto_status_up", autoStatusUp);
                setValue("auto_status_down", autoStatusDown);
              }

              setValue("service", v?.id);

              if (!watch("name") && v?.name) {
                setValue("name", v.name);
              }
            }}
          />
          <ComponentStatusSelectField
            options={[NO_CHANGE_OPTION, ...componentStatuses.filter((s) => s.is_failure)]}
            name={"auto_status_down"}
            disabled={!watch("service")}
            onChange={(value) => {
              if (value) {
                setAutoStatusDown(value.value);
              }
            }}
            label={"If Check fails, set Component status to:"}
            tooltip="Automatically change Component status based on Check state."
          />
          <ComponentStatusSelectField
            options={[NO_CHANGE_OPTION, ...componentStatuses.filter((s) => s.is_operational)]}
            name={"auto_status_up"}
            disabled={!watch("service")}
            onChange={(value) => {
              if (value) {
                setAutoStatusUp(value.value);
              }
            }}
            label={"When Check recovers, always set the Component status to:"}
            tooltip="Automatically change Component status to the following state or select No Change for the state to never change until manually adjusted."
          />
        </div>
      </form>
    </FormProvider>
  );
};

export default StatusPageUpdateComponentForm;
